/**
 * The browser Notification API, behind four functions that cannot throw.
 *
 * Every one of these is called from a render path or a poll tick, and the
 * API they wrap is absent or hostile in more environments than it is
 * present in: SSR (the admin renders on a Worker first), jsdom under
 * vitest, an iframe without the permission, Safari before a user gesture,
 * and any profile where notifications are blocked at the OS level. A bare
 * `Notification.permission` read is a `ReferenceError` in the first two of
 * those, which would take down the whole shell for a bell icon.
 *
 * So the rule here is: probe defensively, answer with a value, never
 * throw, and never ask for anything. In particular `requestPermission` is
 * NOT called from this module's own code — it is exported for exactly one
 * caller, the bell's click handler (D11: permission is requested only from
 * a click on the bell, never on load). Chrome and Firefox both now refuse
 * a request that did not come from a user gesture, so asking on load does
 * not merely annoy, it permanently denies.
 */

/** Permission as this app reasons about it. `'unsupported'` is a fourth
 * state the platform type does not have, and conflating it with `'denied'`
 * would make the bell offer an enable button that can never succeed. */
export type NotifyPermission = NotificationPermission | 'unsupported'

/** Is there a usable Notification constructor on this global?
 *
 * `'Notification' in globalThis` rather than `typeof Notification` so SSR
 * and jsdom answer `false` instead of throwing, and a `function` check on
 * top because jsdom has shipped a partial stub before now. */
export function notificationsSupported(): boolean {
  try {
    return (
      'Notification' in globalThis &&
      typeof (globalThis as { Notification?: unknown }).Notification ===
        'function'
    )
  } catch {
    // A sandboxed iframe can throw on the property access itself. There
    // is nothing to report and nothing to retry: no notifications here.
    return false
  }
}

/** The current permission, or `'unsupported'`. Never throws, so it is safe
 * to call during render. */
export function notificationPermission(): NotifyPermission {
  if (!notificationsSupported()) return 'unsupported'
  try {
    return Notification.permission
  } catch {
    return 'unsupported'
  }
}

/**
 * Asks the browser for permission. ONLY call this from a user gesture.
 *
 * The returned promise resolves with the outcome rather than rejecting on
 * refusal — "the operator said no" is an answer, not an error, and the
 * bell renders it as a disabled state. A genuine failure (an older Safari
 * that only supports the callback form, a frame that is not allowed to
 * ask) resolves to the permission as it still stands, which is the honest
 * report: nothing changed.
 */
export async function requestNotificationPermission(): Promise<NotifyPermission> {
  if (!notificationsSupported()) return 'unsupported'
  try {
    return await Notification.requestPermission()
  } catch {
    return notificationPermission()
  }
}

/**
 * Fires one OS notification, if we are allowed to.
 *
 * Silently does nothing when unsupported or not granted — the caller is a
 * poll tick with no UI to report into, and a thrown constructor there
 * would break the tick and every source after it.
 *
 * SECURITY: `title` and `body` are written by the callers in
 * `attention-sources.ts`, and MUST NOT carry a message body, a file byte
 * or key material. An OS notification is rendered by the window manager,
 * survives on a lock screen, and is logged by some desktops — it is the
 * least private surface this app has. A subject line is plaintext on the
 * wire already and is the most that belongs here.
 */
export function showNotification(
  title: string,
  options: { body: string; tag: string },
): void {
  if (notificationPermission() !== 'granted') return
  try {
    // `tag` collapses repeats of the same event into one OS-level
    // notification, which is a second line of defence behind the
    // localStorage dedupe: the dedupe stops us CREATING a duplicate, the
    // tag stops a duplicate STACKING if we ever do.
    new Notification(title, { body: options.body, tag: options.tag })
  } catch {
    // Constructing a Notification throws on some mobile browsers even
    // when permission reads `granted` (they require a service worker).
    // There is no fallback worth building and no action for the
    // operator: the in-app badge already carries the same count.
  }
}
